import { biomeFor } from "./biomes";

let ctx = null;
let muted = false;
const vols = { music: parseFloat(localStorage.getItem("tbq_music") ?? "0.8"), sfx: parseFloat(localStorage.getItem("tbq_sfx") ?? "0.8") };
export const getVolumes = () => ({ ...vols });
export const setVolume = (kind, v) => {
  vols[kind] = v;
  localStorage.setItem(`tbq_${kind}`, String(v));
  if (kind === "music" && master) master.gain.value = v * 1.1;
};

export const setMuted = (m) => { muted = m; };
export const isMuted = () => muted;

const tone = (freq, dur, type = "square", vol = 0.06, slide = 0, delay = 0) => {
  if (muted) return;
  try {
    ctx = ctx || new (window.AudioContext || window.webkitAudioContext)();
    const t = ctx.currentTime + delay;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, t);
    if (slide) o.frequency.exponentialRampToValueAtTime(Math.max(30, freq + slide), t + dur);
    g.gain.setValueAtTime(Math.max(0.0001, vol * vols.sfx * 1.25), t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(ctx.destination);
    o.start(t);
    o.stop(t + dur);
  } catch (e) { /* audio unavailable */ }
};

const SOUNDS = {
  swing: () => tone(420, 0.08, "triangle", 0.04, -250),
  hit: () => tone(180, 0.08, "square", 0.05, -100),
  kill: () => { tone(300, 0.06); tone(520, 0.1, "square", 0.05, 200, 0.05); },
  toast: () => { tone(880, 0.07, "square", 0.04); tone(1320, 0.1, "square", 0.04, 0, 0.06); },
  hurt: () => tone(140, 0.25, "sawtooth", 0.07, -90),
  die: () => [440, 330, 220, 110].forEach((f, i) => tone(f, 0.18, "square", 0.06, 0, i * 0.15)),
  level: () => [523, 659, 784].forEach((f, i) => tone(f, 0.12, "square", 0.05, 0, i * 0.09)),
  clear: () => [523, 659, 784, 1046].forEach((f, i) => tone(f, 0.15, "triangle", 0.07, 0, i * 0.1)),
  weapon: () => [392, 523, 659, 784, 1046].forEach((f, i) => tone(f, 0.12, "square", 0.05, 0, i * 0.07)),
  heart: () => tone(660, 0.2, "sine", 0.08, 300),
  shoot: () => tone(600, 0.06, "sawtooth", 0.025, -300),
  boss: () => tone(80, 0.6, "sawtooth", 0.08, -40),
  victory: () => [523, 523, 659, 784, 659, 784, 1046].forEach((f, i) => tone(f, 0.22, "triangle", 0.08, 0, i * 0.18)),
};

export const sfx = (name) => SOUNDS[name]?.();

// ---------- Music: original JRPG-style tracks, generated from per-level song specs ----------
const MODES = {
  major: [0, 2, 4, 5, 7, 9, 11],
  minor: [0, 2, 3, 5, 7, 8, 10],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  harmonic: [0, 2, 3, 5, 7, 8, 11],
};
export const SONGS = {
  1: { name: "Pantry Prelude", bpm: 112, root: 60, mode: "major", prog: [0, 4, 5, 3], arp: "up" },
  2: { name: "Crust of the Kingdom", bpm: 118, root: 55, mode: "major", prog: [0, 5, 3, 4], arp: "updown" },
  3: { name: "Ember Cavern", bpm: 104, root: 57, mode: "harmonic", prog: [0, 5, 2, 4], arp: "broken" },
  4: { name: "Cellar of Golden Light", bpm: 100, root: 62, mode: "dorian", prog: [0, 3, 0, 6], arp: "up" },
  5: { name: "Sticky Marsh Waltz", bpm: 96, root: 52, mode: "harmonic", prog: [0, 3, 5, 4], arp: "updown" },
  6: { name: "Requiem of Crumbs", bpm: 90, root: 59, mode: "harmonic", prog: [0, 5, 3, 4], arp: "broken" },
  7: { name: "Spores in Bloom", bpm: 108, root: 53, mode: "minor", prog: [0, 6, 5, 4], arp: "up" },
  8: { name: "Inferno Overture", bpm: 132, root: 48, mode: "harmonic", prog: [0, 5, 6, 4], arp: "broken" },
  9: { name: "March of the Bread Fortress", bpm: 126, root: 50, mode: "minor", prog: [0, 6, 5, 6], arp: "updown" },
  10: { name: "Spire of Sourdough", bpm: 116, root: 57, mode: "dorian", prog: [0, 6, 3, 4], arp: "up" },
  11: { name: "Cinnamon Labyrinth", bpm: 110, root: 55, mode: "harmonic", prog: [0, 3, 4, 0], arp: "broken" },
  12: { name: "Ballad of the Burnt Baron", bpm: 98, root: 50, mode: "harmonic", prog: [0, 5, 6, 4], arp: "updown" },
  13: { name: "Waffle Iron Wasteland", bpm: 124, root: 53, mode: "minor", prog: [0, 5, 2, 6], arp: "up" },
  14: { name: "Crouton Canyon Run", bpm: 130, root: 58, mode: "dorian", prog: [0, 3, 6, 4], arp: "updown" },
  15: { name: "The Last Toast", bpm: 140, root: 52, mode: "harmonic", prog: [0, 5, 3, 4], arp: "broken" },
  ending: { name: "Butter's Lullaby", bpm: 84, root: 60, mode: "major", prog: [0, 3, 4, 0, 5, 3, 1, 4], arp: "updown" },
};
const songKey = (level) => (SONGS[level] ? level : ((level - 16) % 15) + 1);
export const songName = (level, boss) => {
  const n = SONGS[songKey(level)].name + (level > 15 ? " (Encore)" : "");
  return `${boss ? `${n} (Boss Battle)` : n} · ${INSTR[biomeFor(level).kind].label}`;
};

const midiHz = (m) => 440 * Math.pow(2, (m - 69) / 12);
const seeded = (a) => () => { a = (a + 0x6d2b79f5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };

const scaleNote = (song, degree, octave = 0) => {
  const sc = MODES[song.mode];
  const o = Math.floor(degree / 7), d = ((degree % 7) + 7) % 7;
  return song.root + sc[d] + 12 * (o + octave);
};
const chordNotes = (song, deg) => [0, 2, 4].map((i) => scaleNote(song, deg + i));

const makeMelody = (song, seed) => {
  const r = seeded(seed);
  const phrase = (bars, startDeg) => {
    const notes = [];
    let deg = startDeg;
    for (let b = 0; b < bars; b++) {
      const chordDeg = song.prog[b % song.prog.length];
      for (let s = 0; s < 16; s += 2) {
        if (s === 0) { deg = chordDeg + [0, 2, 4][Math.floor(r() * 3)] + 7; notes.push({ step: b * 16 + s, deg, len: r() < 0.5 ? 4 : 2 }); s += 2; continue; }
        if (r() < 0.28) continue;
        deg += Math.floor(r() * 5) - 2;
        deg = Math.max(chordDeg + 5, Math.min(chordDeg + 13, deg));
        const len = r() < 0.2 ? 4 : 2;
        notes.push({ step: b * 16 + s, deg, len });
        if (len === 4) s += 2;
      }
    }
    return notes;
  };
  const bars = song.prog.length;
  const a = phrase(bars, 7);
  const b = phrase(bars, 9).map((n) => ({ ...n, step: n.step + bars * 16 }));
  return { notes: [...a, ...b], length: bars * 32 };
};

let noiseBuf = null;
const getNoise = () => {
  if (!noiseBuf) {
    noiseBuf = ctx.createBuffer(1, ctx.sampleRate * 0.2, ctx.sampleRate);
    const d = noiseBuf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  }
  return noiseBuf;
};

let master = null;
let music = null;
const out = () => {
  if (!master) { master = ctx.createGain(); master.gain.value = vols.music * 1.1; master.connect(ctx.destination); }
  return master;
};

const voice = (freq, t, dur, { type = "triangle", vol = 0.05, attack = 0.01, detune = 0, cutoff = 0, vib = 0, bend = 0 } = {}) => {
  const o = ctx.createOscillator(), g = ctx.createGain();
  o.type = type; o.detune.value = detune;
  if (bend) { o.frequency.setValueAtTime(freq * bend, t); o.frequency.exponentialRampToValueAtTime(freq, t + 0.08); }
  else o.frequency.setValueAtTime(freq, t);
  if (vib) {
    const l = ctx.createOscillator(), lg = ctx.createGain();
    l.frequency.value = 5.5; lg.gain.value = freq * vib;
    l.connect(lg).connect(o.frequency); l.start(t); l.stop(t + dur + 0.05);
  }
  g.gain.setValueAtTime(0.0001, t);
  g.gain.linearRampToValueAtTime(vol, t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  let node = o;
  if (cutoff) { const f = ctx.createBiquadFilter(); f.type = "lowpass"; f.frequency.value = cutoff; o.connect(f); node = f; }
  node.connect(g).connect(out());
  o.start(t); o.stop(t + dur + 0.05);
};

const hat = (t, vol = 0.015) => {
  const src = ctx.createBufferSource(), f = ctx.createBiquadFilter(), g = ctx.createGain();
  src.buffer = getNoise(); f.type = "highpass"; f.frequency.value = 7000;
  g.gain.setValueAtTime(vol, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.05);
  src.connect(f).connect(g).connect(out()); src.start(t); src.stop(t + 0.06);
};

const kick = (t) => {
  const o = ctx.createOscillator(), g = ctx.createGain();
  o.frequency.setValueAtTime(140, t); o.frequency.exponentialRampToValueAtTime(40, t + 0.12);
  g.gain.setValueAtTime(0.12, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.15);
  o.connect(g).connect(out()); o.start(t); o.stop(t + 0.16);
};

const noiseHit = (t, { type = "highpass", freq = 7000, vol = 0.015, dur = 0.05, q = 1 } = {}) => {
  const src = ctx.createBufferSource(), f = ctx.createBiquadFilter(), g = ctx.createGain();
  src.buffer = getNoise(); f.type = type; f.frequency.value = freq; f.Q.value = q;
  g.gain.setValueAtTime(vol, t); g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  src.connect(f).connect(g).connect(out()); src.start(t); src.stop(t + dur + 0.01);
};
const tom = (t, f, vol = 0.1, dur = 0.22) => {
  const o = ctx.createOscillator(), g = ctx.createGain();
  o.type = "sine"; o.frequency.setValueAtTime(f * 1.6, t); o.frequency.exponentialRampToValueAtTime(f, t + 0.06);
  g.gain.setValueAtTime(vol, t); g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  o.connect(g).connect(out()); o.start(t); o.stop(t + dur + 0.02);
};

// each world has its own band: lead + arpeggio instruments, pad colour and percussion groove
const TONES = {
  chip: (f, t, d, v = 1) => { voice(f, t, d, { type: "square", vol: 0.018 * v, attack: 0.02, cutoff: 2600 }); voice(f, t, d, { type: "triangle", vol: 0.02 * v, attack: 0.02, detune: 6 }); },
  marimba: (f, t, d, v = 1) => { const dd = Math.min(d, 0.4); voice(f, t, dd, { type: "sine", vol: 0.05 * v, attack: 0.004 }); voice(f * 4, t, dd * 0.4, { type: "sine", vol: 0.012 * v, attack: 0.002 }); },
  steel: (f, t, d, v = 1) => { const dd = Math.max(0.45, d); voice(f, t, dd, { type: "triangle", vol: 0.04 * v, attack: 0.004 }); voice(f * 2, t, dd * 0.7, { type: "sine", vol: 0.02 * v, attack: 0.004 }); voice(f * 3.01, t, dd * 0.4, { type: "sine", vol: 0.007 * v, attack: 0.004 }); },
  flute: (f, t, d, v = 1) => { voice(f, t, d * 1.2, { type: "sine", vol: 0.045 * v, attack: 0.06, vib: 0.008 }); voice(f * 2, t, d, { type: "sine", vol: 0.006 * v, attack: 0.08 }); },
  harp: (f, t, d, v = 1) => voice(f, t, Math.max(0.5, d), { type: "triangle", vol: 0.035 * v, attack: 0.003 }),
  banjo: (f, t, d, v = 1) => { voice(f, t, 0.28, { type: "sawtooth", vol: 0.02 * v, attack: 0.002, cutoff: 1900 }); voice(f, t, 0.22, { type: "square", vol: 0.008 * v, attack: 0.002, detune: 9, cutoff: 2600 }); },
  sitar: (f, t, d, v = 1) => { voice(f, t, Math.max(0.4, d), { type: "sawtooth", vol: 0.022 * v, attack: 0.003, cutoff: 2400, bend: 1.04 }); voice(f * 2, t, 0.3, { type: "sine", vol: 0.008 * v, attack: 0.003, bend: 1.04 }); },
  bells: (f, t, d, v = 1) => { voice(f * 2, t, 1.1, { type: "sine", vol: 0.03 * v, attack: 0.002 }); voice(f * 2 * 2.76, t, 0.5, { type: "sine", vol: 0.008 * v, attack: 0.002 }); },
  guitar: (f, t, d, v = 1) => { [1, 1.5].forEach((k) => { voice(f * k, t, d * 1.1, { type: "sawtooth", vol: 0.016 * v, attack: 0.005, cutoff: 1500, detune: 10 }); voice(f * k, t, d * 1.1, { type: "square", vol: 0.01 * v, attack: 0.005, cutoff: 1200, detune: -10 }); }); },
  epiano: (f, t, d, v = 1) => { voice(f, t, Math.max(0.6, d), { type: "sine", vol: 0.04 * v, attack: 0.004, vib: 0.003 }); voice(f * 2, t, 0.25, { type: "triangle", vol: 0.01 * v, attack: 0.003 }); },
  theremin: (f, t, d, v = 1) => voice(f, t, d * 1.3, { type: "sine", vol: 0.04 * v, attack: 0.09, vib: 0.02 }),
  arpTri: (f, t, d, v = 1) => voice(f, t, d, { type: "triangle", vol: 0.035 * v }),
  arpSine: (f, t, d, v = 1) => voice(f, t, d, { type: "sine", vol: 0.03 * v, vib: 0.01 }),
};
const GROOVES = {
  kitchen: (s, t, m) => { if (m.song.bpm > 110 && s % 4 === 2) hat(t, 0.01); },
  jungle: (s, t) => {
    if (s % 4 === 0) tom(t, s % 8 === 0 ? 90 : 130, 0.11);
    if (s === 6 || s === 14) tom(t, 170, 0.07, 0.15);
    if (s % 2 === 1) noiseHit(t, { type: "bandpass", freq: 5000, vol: 0.012, dur: 0.04 });
  },
  beach: (s, t) => {
    if (s === 0 || s === 8) kick(t);
    if ([3, 6, 10, 11, 14].includes(s)) tom(t, s % 2 ? 260 : 200, 0.05, 0.12);
    if (s % 2 === 0) noiseHit(t, { type: "highpass", freq: 6000, vol: 0.01, dur: 0.06 });
  },
  mountains: (s, t) => { if (s === 0) tom(t, 80, 0.09, 0.4); if (s === 8) tom(t, 100, 0.06, 0.3); if (s % 4 === 2) noiseHit(t, { freq: 8000, vol: 0.006 }); },
  swamp: (s, t) => { if (s % 8 === 0) tom(t, 65, 0.1, 0.3); if (s % 4 === 2) noiseHit(t, { type: "bandpass", freq: 3500, vol: 0.02, dur: 0.07, q: 3 }); },
  desert: (s, t) => {
    if (s === 0 || s === 6) tom(t, 85, 0.11, 0.25);
    if ([4, 10, 12, 14].includes(s)) noiseHit(t, { type: "bandpass", freq: 3200, vol: 0.03, dur: 0.05, q: 4 });
  },
  snow: (s, t) => { if (s % 2 === 0) noiseHit(t, { freq: 9500, vol: s % 4 === 0 ? 0.012 : 0.006, dur: 0.08 }); if (s === 0) tom(t, 70, 0.05, 0.4); },
  volcano: (s, t) => {
    if ([0, 3, 8, 11].includes(s)) tom(t, 55, 0.14, 0.35);
    if (s === 4 || s === 12) noiseHit(t, { type: "bandpass", freq: 1800, vol: 0.05, dur: 0.12 });
    if (s % 2 === 0) hat(t, 0.012);
  },
  city: (s, t) => {
    if (s === 0 || s === 10) kick(t);
    if (s === 4 || s === 12) noiseHit(t, { type: "bandpass", freq: 1900, vol: 0.04, dur: 0.1 });
    if (s % 4 === 0 || s % 4 === 3) hat(t, 0.012);
  },
  space: (s, t) => { if (s === 0) kick(t); if (s % 8 === 4) voice(1760, t, 0.15, { type: "sine", vol: 0.012 }); },
};
export const INSTR = {
  kitchen: { label: "Chiptune Kitchen Band", lead: "chip", arp: "arpTri", pad: "sawtooth" },
  jungle: { label: "Jungle Drums & Marimba", lead: "marimba", arp: "marimba", pad: "triangle" },
  beach: { label: "Steel Drum Island Band", lead: "steel", arp: "steel", pad: "sine" },
  mountains: { label: "Mountain Flute & Harp", lead: "flute", arp: "harp", pad: "sawtooth" },
  swamp: { label: "Bayou Banjo & Washboard", lead: "banjo", arp: "banjo", pad: "triangle" },
  desert: { label: "Desert Sitar & Hand Drums", lead: "sitar", arp: "sitar", pad: "sawtooth" },
  snow: { label: "Snowy Sleigh Bells", lead: "bells", arp: "harp", pad: "sine" },
  volcano: { label: "Volcano Rock & Taiko", lead: "guitar", arp: "arpTri", pad: "sawtooth" },
  city: { label: "Midnight City Jazz", lead: "epiano", arp: "epiano", pad: "triangle" },
  space: { label: "Cosmic Theremin", lead: "theremin", arp: "arpSine", pad: "sine" },
};

const ARPS = { up: [0, 1, 2, 3, 0, 1, 2, 3], updown: [0, 1, 2, 3, 2, 1, 0, 1], broken: [0, 2, 1, 3, 0, 2, 1, 3] };

const scheduleStep = (m, step, t) => {
  const { song, boss, melody, sixteenth, inst } = m;
  const bar = Math.floor(step / 16) % song.prog.length;
  const s = step % 16;
  const chord = chordNotes(song, song.prog[bar]);
  const arpTones = [...chord, chord[0] + 12];
  if (s % 2 === 0) {
    const n = arpTones[ARPS[song.arp][(s / 2) % 8]] + 12;
    TONES[inst.arp](midiHz(n), t, sixteenth * 2.6, inst.arp === inst.lead ? 0.6 : 1);
  }
  if (s === 0) {
    chord.forEach((n) => {
      const pv = inst.pad === "sawtooth" ? 1 : 1.8;
      voice(midiHz(n), t, sixteenth * 16, { type: inst.pad, vol: 0.012 * pv, attack: sixteenth * 4, cutoff: 1100 });
      voice(midiHz(n), t, sixteenth * 16, { type: inst.pad, vol: 0.01 * pv, attack: sixteenth * 4, detune: 8, cutoff: 1100 });
    });
  }
  const bassEvery = boss ? 2 : 8;
  if (s % bassEvery === 0) voice(midiHz(chord[0] - 12 - (boss && s % 4 === 2 ? -12 : 0)), t, sixteenth * (boss ? 1.8 : 7), { type: "triangle", vol: 0.07 });
  const mstep = step % melody.length;
  melody.notes.filter((n) => n.step === mstep).forEach((n) => {
    TONES[inst.lead](midiHz(scaleNote(song, n.deg)), t, sixteenth * n.len * 1.1);
  });
  GROOVES[m.kind](s, t, m);
  if (boss) {
    if (s % 4 === 0) kick(t);
    if (s % 2 === 1) hat(t);
  }
};

export const playMusic = (mode = "level", level = 1) => {
  stopMusic();
  try { ctx = ctx || new (window.AudioContext || window.webkitAudioContext)(); } catch (e) { return; }
  const key = mode === "ending" ? "ending" : songKey(level);
  const base = SONGS[key] || SONGS[1];
  const boss = mode === "boss";
  const song = boss ? { ...base, bpm: Math.round(base.bpm * 1.3), mode: base.mode === "major" ? "harmonic" : base.mode } : base;
  const kind = mode === "ending" ? "kitchen" : biomeFor(level).kind;
  const m = { kind, inst: INSTR[kind], song, boss, melody: makeMelody(song, (typeof key === "number" ? key : 99) * 977 + (boss ? 31 : 0)), sixteenth: 60 / song.bpm / 4, step: 0, next: ctx.currentTime + 0.1 };
  m.timer = setInterval(() => {
    while (m.next < ctx.currentTime + 0.12) {
      if (!muted) scheduleStep(m, m.step, m.next);
      m.step++; m.next += m.sixteenth;
    }
  }, 25);
  music = m;
};

export const stopMusic = () => { if (music) clearInterval(music.timer); music = null; };
